/**
 * @fileoverview Link Diagram Command
 * 
 * Command to link a shape to another diagram, enabling drill-down navigation.
 * 
 * @see Issue #789 - Universal Diagram Linking System
 */

import { ICommand } from './ICommand';
import { getNodeMutationManager } from './mutations/NodeMutationManager';
import { getCookie } from '../../lib/cookies';
import type { CreateDiagramLinkInput } from '../../domains/diagram/models/DiagramLink';

interface LinkDiagramCommandParams {
  sourceDiagramId: string;
  sourceShapeId: string;
  targetDiagramId: string;
  targetDiagramName: string;
  linkType?: 'subprocess' | 'reference' | 'detail';
  metadata?: Record<string, unknown>;
}

export class LinkDiagramCommand implements ICommand {
  id: string;
  name = 'Link Diagram';
  description?: string;
  timestamp: number;
  
  private linkId: string | null = null; // Store created link ID for undo
  private originalAttrs: Record<string, any> | null = null;

  constructor(private params: LinkDiagramCommandParams) {
    this.id = `link-diagram-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.description = `Link shape to ${params.targetDiagramName}`;
    this.timestamp = Date.now();
  }

  async execute(): Promise<void> {
    const mutationManager = getNodeMutationManager();
    const node = mutationManager.getNode(this.params.sourceShapeId);
    
    if (!node) {
      throw new Error(`Shape ${this.params.sourceShapeId} not found`);
    }

    // Store original attrs for undo
    this.originalAttrs = { ...((node as any).attrs || {}) };

    // Create link via API
    const token = getCookie('gravity_token');
    if (!token) {
      throw new Error('Not authenticated');
    }

    const input: CreateDiagramLinkInput = {
      sourceDiagramId: this.params.sourceDiagramId,
      sourceShapeId: this.params.sourceShapeId,
      targetDiagramId: this.params.targetDiagramId,
      linkType: this.params.linkType || 'subprocess',
      metadata: this.params.metadata,
    };

    const response = await fetch(`/api/diagrams/${this.params.sourceDiagramId}/diagram-links`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(input),
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create link' }));
      throw new Error(error.error || 'Failed to create link');
    }

    const result = await response.json();
    this.linkId = result.link.id;

    // Update shape metadata to store link information
    const updatedAttrs = {
      ...this.originalAttrs,
      linkedDiagramId: this.params.targetDiagramId,
      linkedDiagramName: this.params.targetDiagramName,
      linkId: this.linkId,
      linkType: this.params.linkType || 'subprocess',
    };

    // Update shape via mutation manager
    mutationManager.applyNodeChange(this.params.sourceShapeId, {
      attrs: updatedAttrs,
    } as any);
  }

  async undo(): Promise<void> {
    if (!this.linkId) {
      // Link wasn't created, nothing to undo
      return;
    }

    const mutationManager = getNodeMutationManager();

    // Delete link via API
    const token = getCookie('gravity_token');
    if (token) {
      try {
        await fetch(`/api/diagram-links/${this.linkId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include',
        });
      } catch (error) {
        // Log but don't throw - we still want to restore the shape state
        console.error('Failed to delete link on undo:', error);
      }
    }

    // Restore original shape attrs
    if (this.originalAttrs !== null) {
      mutationManager.applyNodeChange(this.params.sourceShapeId, {
        attrs: this.originalAttrs,
      } as any);
    }
  }

  async redo(): Promise<void> {
    await this.execute();
  }

  getMetadata(): Record<string, any> {
    return {
      sourceShapeId: this.params.sourceShapeId,
      targetDiagramId: this.params.targetDiagramId,
      targetDiagramName: this.params.targetDiagramName,
      linkId: this.linkId,
    };
  }
}
